import { Stack } from '../layout/Stack.js'
import { Text } from '../ui/Typography.js'
import { adminIllustration } from '../../lib/adminAssets.js'

export interface EmptyStateProps {
  title: React.ReactNode
  /**
   * What would be here, or what to try instead.
   *
   * The product's rule, and it is not decoration: *"a search that found nothing
   * says what to try instead"*, and an empty list *"names itself rather than
   * saying today on all three"*. A bare "No results" is a screen that looks broken.
   */
  description: React.ReactNode
  action?: React.ReactNode
  icon?: React.ReactNode
  /**
   * A shared admin asset key (for example "bookings" or "records").
   * Existing utility-flow paths retain their original artwork.
   * Artwork is decorative because the title already describes the state.
   */
  illustration?: string
}

/** What a list says when it has nothing in it. Never an empty table with headings. */
export function EmptyState({ title, description, action, icon, illustration }: EmptyStateProps) {
  return (
    <Stack
      align="center"
      gap={3}
      className="rounded-lg border border-dashed border-hairline-strong px-6 py-12 text-center"
    >
      {illustration ? (
        <img
          src={adminIllustration(illustration)}
          alt=""
          width={180}
          height={135}
          decoding="async"
          className="mb-1 h-auto w-36 max-w-full object-contain sm:w-[180px]"
        />
      ) : (
        icon && <div className="text-ink-muted [&_svg]:size-6">{icon}</div>
      )}
      <Text as="h3" variant="card-title">
        {title}
      </Text>
      {/* A `div` when the description is more than a sentence: 9.9 passes a
          stack of paragraphs, and a `<p>` holding `<p>`s is invalid HTML that
          React warned about on every refusal. */}
      <Text
        as={typeof description === 'string' ? 'p' : 'div'}
        variant="secondary"
        className="max-w-prose"
      >
        {description}
      </Text>
      {action}
    </Stack>
  )
}
